import { ReactElement, useState } from 'react';
import styled from 'styled-components';
import { saveAs } from 'file-saver';

import { AxiosResponse } from 'axios';
import Icon from '@mdi/react';

import { Box, Flex, Loading, Typography } from 'components';
import { DownloadsItemState } from 'features/downloads';
import { useSchool } from 'features/school';
import { request } from 'app/utils';
import { useTracking } from 'features/tracking';
import { mdiFileDownloadOutline } from '@mdi/js';

const DocumentTypography = styled('a')`
  color: ${({ theme }) => theme.colors.white80};
  cursor: pointer;
  line-height: 0.5;

  &:hover {
    text-decoration: underline;
  }
`;

const Container = styled(Flex)`
  &:hover {
    text-decoration: underline;
    background-color: ${({ theme }) => theme.colors.gray_600};
  }
`;

const DownloadIcon = styled(Icon).attrs({
  path: mdiFileDownloadOutline,
})`
  display: block;
  color: ${({ theme }) => theme.colors.gray_500};
  &:hover {
    color: ${({ theme }) => theme.colors.primary_200};
  }
  flex-shrink: 0;

  ${({ theme: { mediaQueries } }) => {
    return `
      width: 1.3rem;
      height: 1.3rem;

      ${mediaQueries.desktop} {
        width: 1.5rem;
        height: 1.5rem;
      }
    `;
  }}
`;

interface DownloadsBoxContentProps {
  downloads: DownloadsItemState[];
}

const ContentDownload = ({ downloads }: DownloadsBoxContentProps): ReactElement => {
  const { apiGatewayUrl } = useSchool();
  const [loading, setLoading] = useState(false);
  const { eventTracker } = useTracking();
  const [percentage, setPercentage] = useState(0);

  const getDownload = (downloadUrl: string, downloadFileName: string) => {
    setLoading(true);

    eventTracker({ category: 'Conteúdos', action: 'Download', label: `Arquivo: ${downloadFileName}` });

    request({
      url: downloadUrl,
      method: 'get',
      baseURL: apiGatewayUrl,
      responseType: 'blob',
      onDownloadProgress: (progressEvent) => {
        const { loaded, total } = progressEvent;
        let percentCompleted = total === undefined ? 0 : Math.floor((loaded / total) * 100);

        setPercentage(percentCompleted);
      },
    })
      .then((response: AxiosResponse<Blob>) => {
        setLoading(false);

        saveAs(response.data, downloadFileName);

        setTimeout(() => {
          setPercentage(0);
        }, 0);
      })
      .catch((error) => {
        setLoading(false);
        setPercentage(0);
        // console.log(error);
      });
  };

  return (
    <>
      {loading && <Loading alpha={0.8} label={`Por favor, aguarde.<br />${percentage ? `${percentage}%` : ``}`} />}

      <Flex flexGrow={0} flexDirection="column" backgroundColor="dark_150" flex={1}>
        <Flex flexDirection="column" backgroundColor="dark_150">
          {downloads.map((download, index) => {
            return (
              <Flex key={`content-downloads-item-${index.toString()}`} flexGrow={0} flexWrap="nowrap" alignItems="center">
                <Container
                  borderBottomWidth={1}
                  borderBottomColor="white25"
                  borderBottomStyle="solid"
                  flexDirection="row"
                  padding={3}
                  alignItems="center"
                  justifyContent="space-between"
                  style={{ cursor: 'pointer' }}
                >
                  {download.downloadType === 0 && (
                    <DocumentTypography key={index.toString()} href={download.downloadUrl} target="_blank">
                      <Typography type="type1" color="white" fontWeight="bold" lineHeight={1.3}>
                        {download.name}
                      </Typography>
                    </DocumentTypography>
                  )}

                  {(download.downloadType === 1 || download.downloadType === 2) && (
                    <DocumentTypography key={index.toString()} onClick={() => getDownload(download.downloadUrl, download.downloadFileName)}>
                      <Typography type="type1" color="white" fontWeight="bold" lineHeight={1.3}>
                        {download.name}
                      </Typography>
                    </DocumentTypography>
                  )}
                  <DownloadIcon />
                </Container>
              </Flex>
            );
          })}
        </Flex>
      </Flex>
    </>
  );
};

export default ContentDownload;
